import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  useDeleteAIReports,
  useGetAIReportDetail,
} from 'services/personalised-ai-report/hooks';
import Loader from 'components/Loader';
import useAuthStore from 'store/auth';
import ReportDetailInfo from 'components/reports/ReportDetailInfo';
import toast from 'react-hot-toast';

enum TABS {
  INSIGHTS = 'insights',
  GENERATED_REPORT = 'generated_report',
  INPUT_PARAMETERS = 'input_parameters',
}

export default function ReportDetail({
  reportTokenId,
}: {
  reportTokenId: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const tabs = [
    { label: t('insights'), value: TABS.INSIGHTS },
    {
      label: t('generated_report'),
      value: TABS.GENERATED_REPORT,
    },
    {
      label: t('input_parameters'),
      value: TABS.INPUT_PARAMETERS,
    },
  ];

  const [selectedTab, setSelectedTab] = useState<TABS>(TABS.INSIGHTS);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const profileId = useAuthStore((store) => store.currentUserProfileId);

  const {
    data: reportDetail,
    isFetching,
    isError,
  } = useGetAIReportDetail({
    tokenId: reportTokenId,
  });

  const { mutate: deleteReports } = useDeleteAIReports({
    onMutate: () => setIsDeleting(true),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['inifinite-ai-report', profileId],
      });
      setIsDeleting(false);
      toast.success(t('report_deleted_msg'));
    },
    onError: () => setIsDeleting(false),
  });

  useEffect(() => {
    if (
      reportDetail?.[0]?.ai_insights &&
      Object.keys(reportDetail?.[0]?.ai_insights)?.length
    )
      setSelectedTab(TABS.INSIGHTS);
    else setSelectedTab(TABS.GENERATED_REPORT);
  }, [reportTokenId, reportDetail]);

  const onDelete = () => deleteReports([reportTokenId]);

  if (isFetching) {
    return <Loader />;
  }

  return (
    <ReportDetailInfo
      isDeleting={isDeleting}
      isError={isError}
      onDelete={onDelete}
      reportDetail={reportDetail}
      tabs={tabs}
      reportType='ai'
      selectedTab={selectedTab}
      setSelectedTab={setSelectedTab}
    />
  );
}
